<template>
  <div>
    <v-card
      class="mx-auto"
    >
      <v-card-title>
        Projects list
        <v-spacer></v-spacer>
        <v-btn
          small
          outlined
          color="primary"
          class="mr-2"
          @click="resetSavedState"
        >
          Reset
        </v-btn>
        <v-btn icon color="success" @click="allProjectsDialog = true">
          <v-icon>mdi-plus-circle</v-icon>
        </v-btn>
      </v-card-title>
      <v-sheet class="pa-4 primary">
        <v-text-field
          v-model="search"
          label="Search project"
          dark
          flat
          solo-inverted
          hide-details
          clearable
          clear-icon="mdi-close-circle-outline"
        ></v-text-field>
        <v-checkbox
          v-if="multiple"
          v-model="selectMultiple"
          dark
          label="Select multiple"
          hide-details
          dense
        ></v-checkbox>
      </v-sheet>
      <v-virtual-scroll
        :items="filteredProjectsList"
        item-height="60"
        min-height="60"
        max-height="500"
        :key="updateKey"
      >
        <template v-slot:default="{ item }">
          <v-list-item
            link
            :input-value="projectIdsEqual(item.Project_ID, selectedProject)"
            @click.prevent="setProject(item, true)"
            color="primary"
            :disabled="projectIdsEqual(item.Project_ID, blockedProject) || isLoading"
          >
            <v-list-item-icon>
              <v-checkbox
                v-if="selectMultiple"
                v-model="selectedProjects"
                :value="item.Project_ID"
                class="ma-0"
                dense
                hide-details
              ></v-checkbox>
              <v-icon>mdi-folder</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title v-text="item.Description"></v-list-item-title>
            </v-list-item-content>

            <v-list-item-action v-if="!simple && projectIdsEqual(item.Project_ID, selectedProject) && !projectIdsEqual(item.Project_ID, blockedProject)" @click.prevent="getDetailInformation">
              <v-btn icon>
                <v-icon color="primary">mdi-alpha-r-circle</v-icon>
              </v-btn>
            </v-list-item-action>
            <v-list-item-action v-if="projectIdsEqual(item.Project_ID, blockedProject)">
              <v-icon
                color="primary"
              >
                mdi-loading mdi-spin
              </v-icon>
            </v-list-item-action>
          </v-list-item>
        </template>
      </v-virtual-scroll>
      <v-divider v-if="selectMultiple && selectedProjects.length > 1"></v-divider>
      <v-card-actions v-if="selectMultiple && selectedProjects.length > 1">
        <v-spacer></v-spacer>
        <v-btn
          small
          color="primary"
          @click="setMultipleProjects"
        >
          Show
        </v-btn>
      </v-card-actions>
    </v-card>
    <ProjectsListSettingsDialog
      v-model="allProjectsDialog"
      @updateProjectsList="getProjectsList"
    />
    </div>
</template>
<script>
import { getApiErrorMessage } from '@/utils/apiError'
import { loadViewState, saveViewState, clearViewState, VIEW_KEYS } from '@/utils/viewStatePersistence'

export default {
  name: 'ProjectsList',
  components: {
    ProjectsListSettingsDialog: () => import('@/components/global/ProjectsListSettingsDialog.vue')
  },
  props: {
    simple: {
      required: false,
      type: Boolean,
      default: false
    },
    multiple: {
      required: false,
      type: Boolean,
      default: false
    },
    persistKey: {
      required: false,
      type: String,
      default: VIEW_KEYS.editTasks
    }
  },
  data () {
    return {
      managedProjects: [],
      search: null,
      updateKey: 1,
      gettingInformation: false,
      selectedProjects: [],
      allProjectsDialog: false,
      selectMultiple: false,
      skippingPersist: false
    }
  },
  computed: {
    blockedProject () {
      return this.$store.getters.getBlockedProject
    },
    selectedProject () {
      return this.$store.getters.getSelectedProject
    },
    selectedProjectName () {
      return this.$store.getters.getSelectedProjectName
    },
    user () {
      return this.$store.getters.getUser
    },
    userLogin () {
      return this.user && this.user.login
    },
    isLoading () {
      return this.$store.getters.getIsLoading
    },
    filteredProjectsList () {
      let res = this.managedProjects
      if (this.search) {
        const rg = new RegExp(this.search, 'gi')
        res = this.managedProjects.filter(item => {
          rg.lastIndex = 0
          return rg.test(item.Description)
        })
      }
      return res.sort((a, b) => {
        const valA = a.Description.toUpperCase()
        const valB = b.Description.toUpperCase()
        if (valA < valB) return -1
        if (valA > valB) return 1
        return 0
      })
    }
  },
  created () {
    const saved = loadViewState(this.persistKey, this.userLogin)
    if (!saved || typeof saved !== 'object' || !Object.keys(saved).length) return

    this.skippingPersist = true
    if (typeof saved.projectsSearch === 'string') {
      this.search = saved.projectsSearch
    }
    if (this.multiple && typeof saved.selectMultiple === 'boolean') {
      this.selectMultiple = saved.selectMultiple
      if (Array.isArray(saved.selectedProjects)) {
        this.selectedProjects = [...saved.selectedProjects]
      }
    }
    this.$nextTick(() => {
      this.skippingPersist = false
    })
  },
  mounted () {
    this.getProjectsList()
  },
  destroyed () {
    this.$store.dispatch('removeUsersOnProject')
    this.$store.dispatch('removeParentTasksOnProject')
  },
  methods: {
    persistProjects (patch) {
      if (this.skippingPersist || !this.userLogin) return
      saveViewState(this.persistKey, this.userLogin, patch)
    },
    resetSavedState () {
      clearViewState(this.persistKey, this.userLogin)
      this.skippingPersist = true
      this.search = null
      this.selectedProjects = []
      this.selectMultiple = false
      if (this.managedProjects.length) {
        const first = this.managedProjects[0]
        // Force selection even if already on first project so hosts remount via key bump
        this.$store.dispatch('setCurrentTasksList', { tasksList: [] })
        this.$store.dispatch('setSelectedProject', { selectedProject: first.Project_ID })
        this.$store.dispatch('setSelectedProjectName', { selectedProjectName: first.Description })
        this.$store.dispatch('removeUsersOnProject')
        this.$store.dispatch('removeParentTasksOnProject')
        this.$store.dispatch('updateTasks', { update: true })
      } else {
        this.$store.dispatch('setSelectedProject', { selectedProject: null })
        this.$store.dispatch('setSelectedProjectName', { selectedProjectName: null })
        this.$store.dispatch('setCurrentTasksList', { tasksList: [] })
      }
      this.$nextTick(() => {
        this.skippingPersist = false
        this.$emit('reset')
      })
    },
    getDetailInformation () {
      this.gettingInformation = true
      this.$store.dispatch('showDetailsDialog', {
        show: {
          show: true,
          repByProject: true
        }
      })
    },
    projectIdsEqual (a, b) {
      if (Array.isArray(a) || Array.isArray(b)) return a === b
      return String(a) === String(b)
    },
    setProject (project, removeUsersAndParentTask) {
      if (this.selectMultiple) return
      if (!this.projectIdsEqual(project.Project_ID, this.selectedProject)) {
        this.$store.dispatch('setCurrentTasksList', { tasksList: [] })
        this.$store.dispatch('setSelectedProject', { selectedProject: project.Project_ID })
        this.$store.dispatch('setSelectedProjectName', { selectedProjectName: project.Description })
        this.persistProjects({
          selectedProject: project.Project_ID,
          selectedProjectName: project.Description,
          selectMultiple: false,
          selectedProjects: []
        })
        if (removeUsersAndParentTask && !this.gettingInformation) {
          this.$store.dispatch('removeUsersOnProject')
          this.$store.dispatch('removeParentTasksOnProject')
        }
        this.gettingInformation = false
      }
    },
    setMultipleProjects () {
      this.$store.dispatch('setSelectedProject', { selectedProject: this.selectedProjects })
      this.$store.dispatch('updateTasks', { update: true })
      this.persistProjects({
        selectedProject: this.selectedProjects,
        selectedProjectName: null,
        selectMultiple: true,
        selectedProjects: this.selectedProjects
      })
    },
    resolveInitialProject () {
      if (!this.managedProjects.length) return null

      const saved = loadViewState(this.persistKey, this.userLogin)
      const savedId = saved && saved.selectedProject
      const vuexId = this.selectedProject

      // Prefer current Vuex selection if still in list (session), else saved, else first
      const candidates = []
      if (vuexId != null && !Array.isArray(vuexId)) candidates.push(vuexId)
      if (Array.isArray(vuexId) && vuexId.length) candidates.push(vuexId[0])
      if (savedId != null && !Array.isArray(savedId)) candidates.push(savedId)
      if (Array.isArray(savedId) && savedId.length) candidates.push(savedId[0])

      for (let i = 0; i < candidates.length; i++) {
        const found = this.managedProjects.find(p => this.projectIdsEqual(p.Project_ID, candidates[i]))
        if (found) return found
      }
      return this.managedProjects[0]
    },
    applyMultiSelectRestore (saved) {
      if (!this.multiple || !saved || !saved.selectMultiple) return false
      const savedIds = Array.isArray(saved.selectedProjects)
        ? saved.selectedProjects
        : (Array.isArray(saved.selectedProject) ? saved.selectedProject : [])
      if (!savedIds.length) return false

      const validIds = savedIds
        .map(id => {
          const match = this.managedProjects.find(p => this.projectIdsEqual(p.Project_ID, id))
          return match ? match.Project_ID : null
        })
        .filter(id => id != null)

      if (!validIds.length) return false

      this.skippingPersist = true
      this.selectMultiple = true
      this.selectedProjects = validIds
      this.$store.dispatch('setSelectedProject', { selectedProject: validIds })
      this.$store.dispatch('setSelectedProjectName', { selectedProjectName: null })
      this.$nextTick(() => {
        this.skippingPersist = false
      })
      return true
    },
    getProjectsList () {
      this.axios.get('/api/managedprojects', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.managedProjects = response.data
        setTimeout(() => {
          const saved = loadViewState(this.persistKey, this.userLogin)
          if (this.applyMultiSelectRestore(saved)) {
            this.updateKey++
            return
          }

          const project = this.resolveInitialProject()
          if (project && !this.projectIdsEqual(project.Project_ID, this.selectedProject)) {
            this.setProject(project)
          } else if (project) {
            // Ensure name/id are persisted even when Vuex already matches
            this.persistProjects({
              selectedProject: project.Project_ID,
              selectedProjectName: project.Description
            })
            if (!this.selectedProjectName) {
              this.$store.dispatch('setSelectedProjectName', { selectedProjectName: project.Description })
            }
          }
          this.updateKey++
        })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    }
  },
  watch: {
    search (val) {
      if (this.skippingPersist) return
      this.persistProjects({ projectsSearch: val || '' })
    },
    selectMultiple (val) {
      if (this.skippingPersist || !this.multiple) return
      this.persistProjects({
        selectMultiple: !!val,
        selectedProjects: val ? this.selectedProjects : []
      })
      if (!val) {
        this.selectedProjects = []
      }
    },
    selectedProjects: {
      deep: true,
      handler (val) {
        if (this.skippingPersist || !this.multiple || !this.selectMultiple) return
        this.persistProjects({
          selectMultiple: true,
          selectedProjects: val || []
        })
      }
    }
  }
}
</script>
